import React from "react";
import type {
    ChatSearchResult,
    TranscriptionSearchResult,
} from "components/Evaluation/Stores/EvalStore";
import { Grid, Typography, useTheme } from "@mui/material";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import parser from "html-react-parser";
import { getSpeakerColorByChannelOrPersona } from "Theme/utils";
import { SpeakerStore } from "components/UI/AcxTranscription/Stores/SpeakerStore/SpeakerStore";
import moment from "moment";
import { ChatPersona } from "components/UI/Chat/AcxChatViewer";
import { IWordsPhrasesModel } from "components/Conversations/Views/Drawer/components/DeepFilter/WordsPhrasesStore";

const WORDS_AND_PHRASES_WORD_LIMIT = 150;

export const WordsAndPhrasesBlurb = ({
    searchResult,
    onClick,
    speakerStore,
    searchItems,
}: {
    searchResult: ChatSearchResult | TranscriptionSearchResult;
    onClick: (
        searchResult: ChatSearchResult | TranscriptionSearchResult,
    ) => void;
    speakerStore: SpeakerStore;
    searchItems: IWordsPhrasesModel[];
}) => {
    const theme = useTheme();

    if (
        searchResult &&
        typeof searchResult === "object" &&
        "stmt" in searchResult
    ) {
        return (
            <Grid
                sx={{
                    padding: 1,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    borderRadius: 1,
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                        cursor: "pointer",
                        backgroundColor: theme.palette.gray[50],
                    },
                }}
                onClick={() => {
                    onClick(searchResult);
                }}
            >
                <Grid sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    {searchResult.blurb && (
                        <Typography
                            sx={{
                                color: getSpeakerColorByChannelOrPersona(
                                    searchResult.channel,
                                ),
                                fontWeight: theme.typography.fontWeightBold,
                            }}
                        >
                            {speakerStore?.speakerChannelMap.get(
                                searchResult.channel,
                            )?.name ??
                                speakerStore?.predictedSpeakerChannelMap.get(
                                    searchResult.channel,
                                ) ??
                                `Speaker ${searchResult.channel + 1}`}
                        </Typography>
                    )}
                    {!searchResult.blurb && (
                        <Typography
                            sx={{
                                color: theme.palette.gray[500],
                            }}
                        >
                            Speaker Unknown
                        </Typography>
                    )}
                    <Typography sx={{ color: theme.palette.gray[500] }}>
                        {durationDisplay(searchResult.startTime)}
                    </Typography>
                </Grid>
                <Grid>
                    {parser(
                        formatTextWithHighlights(
                            searchResult.blurb
                                ? searchResult.blurb.text
                                : searchResult.stmt,
                            searchItems,
                            true,
                        ),
                    )}
                </Grid>
            </Grid>
        );
    } else if (
        searchResult &&
        typeof searchResult === "object" &&
        "text" in searchResult
    ) {
        return (
            <Grid
                sx={{
                    padding: 1,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    transition: "background-color 0.2s ease",
                    borderRadius: 1,
                    "&:hover": {
                        cursor: "pointer",
                        backgroundColor: theme.palette.primary.light,
                    },
                }}
                onClick={() => {
                    onClick(searchResult);
                }}
            >
                <Grid sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    {searchResult.text && (
                        <Typography
                            className="highlightIgnore"
                            sx={{
                                color: getSpeakerColorByChannelOrPersona(
                                    searchResult.from,
                                ),
                                fontWeight: theme.typography.fontWeightBold,
                            }}
                        >
                            {searchResult.from === ChatPersona.Agent
                                ? "Agent"
                                : "Customer"}
                        </Typography>
                    )}
                    <Typography
                        className="highlightIgnore"
                        sx={{ color: theme.palette.gray[500] }}
                    >
                        {moment(searchResult.timestamp).format("h:mm A")}
                    </Typography>
                </Grid>
                <Grid>
                    {parser(
                        formatTextWithHighlights(
                            searchResult.text,
                            searchItems,
                            true,
                        ),
                    )}
                </Grid>
            </Grid>
        );
    }
};

function formatTextWithHighlights(
    originalText: string,
    searchItems: IWordsPhrasesModel[],
    strict?: boolean,
) {
    let returnStatement = originalText.trim();
    // Wrap the whole text for selection color
    returnStatement = `<span class="selectionColors">${returnStatement}</span>`;

    searchItems.forEach((item) => {
        let filterText = item.searchPhrase;
        if (!filterText || item.value === "Does not contain") return;

        let regex: RegExp;
        const escapedFilterText = filterText.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&",
        );

        if (item.value === "Starts with") {
            // Match at the start of a word, allowing punctuation before
            regex = new RegExp(`(\\b${escapedFilterText})`, "gi");
        } else if (strict) {
            // Match as a whole word, allowing punctuation boundaries
            regex = new RegExp(`(\\b${escapedFilterText}\\b)`, "gi");
        } else {
            // Match anywhere
            regex = new RegExp(`(${escapedFilterText})`, "gi");
        }

        // Only highlight if filterText is longer than 2 characters
        if (filterText.length > 2) {
            returnStatement = returnStatement.replace(
                regex,
                `<span class="transcriptionHighlight selectionColors" id="highlightText">$1</span>`,
            );
        }
    });

    // Limit to 150 words
    const returnStatementArray = returnStatement.split(" ");
    if (returnStatementArray.length > WORDS_AND_PHRASES_WORD_LIMIT) {
        returnStatement =
            returnStatementArray
                .slice(0, WORDS_AND_PHRASES_WORD_LIMIT)
                .join(" ") + " ...";
    }

    return returnStatement;
}
